/**
 * Central map configuration.
 *
 * Two backends are supported, picked at runtime:
 *
 *  1. MAPBOX  — used when REACT_APP_MAPBOX_TOKEN is set. Best quality
 *     tiles + the Search Box geocoding API. Free tier 50k loads/100k
 *     geocodes per month.
 *
 *  2. OPEN STREET MAP / MAPLIBRE — fallback when no token is set.
 *     Renders OSM raster tiles via maplibre-gl and uses Nominatim for
 *     geocoding. Zero cost, zero account, but please keep traffic low
 *     and respect https://operations.osmfoundation.org/policies/tiles/.
 *
 * The same React surface (`MapView`, `LocationPicker`,
 * `AddressAutocomplete`) works for both — callers don't need to know
 * which backend is active.
 */
export const MAPBOX_TOKEN: string = process.env.REACT_APP_MAPBOX_TOKEN || '';

export const MAPBOX_STYLE_STREETS = 'mapbox://styles/mapbox/streets-v12';
export const MAPBOX_STYLE_NAV = 'mapbox://styles/mapbox/navigation-day-v1';
export const MAPBOX_STYLE_DARK = 'mapbox://styles/mapbox/dark-v11';

// Hyderabad fallback centre (matches existing app default).
export const DEFAULT_CENTER: { latitude: number; longitude: number } = {
  latitude: 17.385,
  longitude: 78.4867,
};

// India bounding box — biases geocode + autocomplete results.
export const INDIA_BBOX: [number, number, number, number] = [68.0, 6.0, 97.5, 37.5];

export const isMapboxConfigured = (): boolean => MAPBOX_TOKEN.length > 0;

/**
 * Returns true when the OSM/MapLibre fallback is the active map backend.
 * Mutually exclusive with `isMapboxConfigured()` — exactly one is true.
 */
export const isOSMFallback = (): boolean => !isMapboxConfigured();

/**
 * Inline MapLibre style spec that points at OpenStreetMap raster tiles
 * directly. No external style server is required, so this works even
 * when CSP `connect-src` is restrictive — only the tile host needs to
 * be reachable (and it's covered by `img-src https:`).
 *
 * Attribution is mandatory under the OSM tile usage policy and is
 * rendered automatically by the maplibre-gl AttributionControl.
 */
export const OSM_RASTER_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: 'raster' as const,
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster' as const, source: 'osm' }],
};

if (typeof window !== 'undefined') {
  (window as unknown as { __PM_BUILD__?: Record<string, unknown> }).__PM_BUILD__ = {
    ...(window as unknown as { __PM_BUILD__?: Record<string, unknown> }).__PM_BUILD__,
    mapboxConfigured: isMapboxConfigured(),
    mapBackend: isMapboxConfigured() ? 'mapbox' : 'osm',
    builtAt: new Date().toISOString(),
  };
}

if (!isMapboxConfigured() && process.env.NODE_ENV !== 'test') {
  // Informational only now — the OSM fallback renders correctly without
  // any token. We still log so the choice is visible in devtools.
  // eslint-disable-next-line no-console
  console.info(
    '[map] REACT_APP_MAPBOX_TOKEN is not set — using free OpenStreetMap tiles + Nominatim geocoding. ' +
      'Set REACT_APP_MAPBOX_TOKEN to upgrade to Mapbox Streets + Search Box.',
  );
}

